/**
 * The `Duration | Start | End` row shared by Log Time and Edit Time Entry, as a
 * pure state machine, plus the two conversions its callers need at the edges:
 * the row seeded from a stored entry, and the instants a save sends.
 *
 * Both dialogs used to take Hours + Minutes and nothing else, so neither could
 * state a clock time: Log Time pinned every manual entry to `09:00`, and Edit
 * kept whatever the row already carried, so the timesheet printed an interval
 * nobody had chosen — a two-hour entry worked in the evening read
 * "09:00 — 11:00". The data model already holds `startedAt`, `endedAt` and
 * `duration`; this links the three the way Tempo's Log Time form does —
 * Duration is the anchor value, Start the anchor time — so the two dialogs
 * agree to the letter and neither re-implements it. [HW-891]
 *
 * The three linked fields are minute-resolution, because the two time boxes are
 * `HH:mm`. Seconds a timer wrote into an entry are preserved by `entryInstants`
 * rather than by the state machine, which never sees them.
 */

import { viewerTimeZone } from '@/lib/timezone';
import { entryClock, entryDayKey, entryStartWall, wallInstant } from './wall-clock';

/** Anything at or past a full day. The per-entry ceiling the server enforces is
 *  23h 59m 59s, and the minute grammar cannot express a value between 23h 59m
 *  and 24h, so "24h or more" is the reachable boundary — and the message reads
 *  for it. [HW-840] */
const MAX_DURATION_SECONDS = 24 * 60 * 60;
const MINUTES_PER_DAY = 24 * 60;

/**
 * Is a length of work one this form may hand to the API?
 *
 * For a duration read out of the Duration box `parseDuration` answers this on
 * the way in. This is for the other direction: a length already stored, which
 * the timer and the Clockify import can write past the ceiling this form obeys
 * [HW-886], and which the Edit dialog must refuse to re-send rather than offer
 * a Save that can only come back 400.
 */
export function durationWithinLimit(seconds: number): boolean {
  return Number.isFinite(seconds) && seconds > 0 && seconds < MAX_DURATION_SECONDS;
}

/**
 * Shown when the date box is empty or half-typed. Here rather than in either
 * dialog because both print it, and a wording that lives in two components
 * drifts into two.
 */
export const PICK_DATE_MESSAGE = 'Pick a date for this entry.';

export const DURATION_REQUIRED = 'This field is required';
export const DURATION_NOT_A_NUMBER = 'Value must be a number';
/** `"25h exceeds the 23h 59m limit"` — the hours are the ones the value came
 *  to, not necessarily the ones typed: `1500m` is refused as 25h. The limit is
 *  named as the longest entry actually taken rather than as the round day it
 *  falls short of, because at the boundary the round day made the sentence
 *  refute itself: `24h` was answered with "24h exceeds the 24h limit", which
 *  tells the reader nothing about what would be accepted. */
export const durationExceedsLimit = (hours: number): string =>
  `${hours}h exceeds the 23h 59m limit`;
/**
 * Said under the End box, the one place a time field explains itself.
 *
 * It names what is actually impossible rather than the rule it comes from.
 * "An entry cannot cross midnight" was measured under `01:00 — 04:00` with 3h
 * typed and an End of `02:00`: nothing on that screen goes near midnight, and
 * the reader is left looking for a crossing that is not there. What cannot be
 * done is start the entry — 3h before 02:00 is eleven the previous night.
 */
export const STARTS_DAY_BEFORE = 'That would start this entry the day before';

/**
 * Where the current Duration came from.
 *
 * `empty` is a box never filled — a dialog just opened, which has nothing to
 * complain about yet. `cleared` is a box the reader emptied, or one the linking
 * rules emptied under them (an End before the Start): the same absent value,
 * but now worth a message, which is the difference the ticket's two "Duration
 * shows 'This field is required'" rows turn on.
 */
export type DurationSource = 'user' | 'derived' | 'empty' | 'cleared';

export interface LinkedTimeState {
  /** Raw text, as typed — kept verbatim so an in-progress value survives a
   *  relink of the other fields and an invalid one can be shown with its error. */
  duration: string;
  /** Normalised `HH:mm`. */
  start: string;
  /** Normalised `HH:mm`, always readable as `start + duration` once settled. */
  end: string;
  /**
   * Where the current Duration came from. It decides what editing End does:
   * a Duration the user typed is kept and End moves Start; a Duration that is
   * absent or was itself derived from an earlier End edit is recomputed and
   * Start is kept. Tracked as provenance, not by comparing numbers — typing
   * `1h` then editing End to imply `1h` again is still a user Duration.
   */
  durationSource: DurationSource;
}

export type LinkedField = 'duration' | 'start' | 'end';

export interface DurationReading {
  /** Whole seconds, or null when the text does not read as a duration. */
  seconds: number | null;
  /** Why not, phrased for the field. Null when `seconds` is set OR the box is
   *  empty and its emptiness is not yet worth complaining about — callers that
   *  must block a save read `seconds`, not the absence of an error. */
  error: string | null;
}

/** `"2h 30m"`, `"6h"`, `"45m"`, `"0m"`. The canonical spelling a derived or
 *  seeded Duration is shown in. Minute-resolution: seconds are floored away. */
export function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(Math.max(0, totalSeconds) / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || hours === 0) parts.push(`${mins}m`);
  return parts.join(' ');
}

/**
 * Read the Duration box.
 *
 * Grammar: `2h`, `2h 30m`, `90m`, a bare integer read as hours (`2` → 2h), and a
 * decimal separator — comma or dot, they read alike — with an optional `h` after
 * it.
 *
 * HOW MANY DIGITS FOLLOW THE SEPARATOR IS THE WHOLE RULE, decided 2026-09-09.
 *
 *  - ONE digit is a tenth of an hour, which is how Tempo reads it: Dmytro
 *    measured `5.1` coming back as 5h 6m and `5.2` as 5h 12m. So `1.5` is an
 *    hour and a half and `0.5` is thirty minutes. A tenth is six whole minutes,
 *    so nothing has to round.
 *  - TWO digits are minutes, and must be under sixty: `5,18` is 5h 18m, `5,05`
 *    is five past, `5,60` is refused because sixty minutes is not a count of
 *    minutes. That refusal is what keeps `0.75` and `0.90` out of the hazard.
 *
 * KNOWN AND ACCEPTED: the two readings meet at `1.5` against `1.50` — an hour
 * and a half against an hour and fifty, twenty minutes apart on two strings that
 * look almost alike. The box spelling back what it understood is the guard, and
 * it is why this field normalises on blur at all. (Every hours box in the app
 * reads this grammar since HW-974: Show entries' Logged and Billable cells and
 * the Edit dialog's Billable Hours box too, Billable through
 * `readBillableDuration`, which only adds zero. The decimal-hours reader the
 * Reports tab once had, `readBillableHours`, is gone. Decided 2026-10-08.)
 *
 * Still refused: a clock (`1:30`) and days (`1d`). `1:30` is the one unambiguous
 * way to write an hour and a half and is worth taking; raised, not done here.
 *
 * `0` and blank are "This field is required"; a value of a day or more is
 * refused by name.
 */
export function parseDuration(raw: string): DurationReading {
  const text = raw.trim().toLowerCase();
  if (text === '') return { seconds: null, error: null };

  let seconds: number | null = null;

  // `2h`, `2h 30m`, `2h30m` — hours then optional minutes.
  const hm = text.match(/^(\d+)\s*h(?:\s*(\d+)\s*m)?$/);
  // `90m` — minutes only.
  const m = text.match(/^(\d+)\s*m$/);
  // `5,18` or `5.18` — hours, a separator, exactly two digits of MINUTES.
  const split = text.match(/^(\d+)[.,](\d{2})h?$/);
  // `5,1` or `5.1` — hours and a TENTH of an hour, six minutes to the tenth.
  const tenth = text.match(/^(\d+)[.,](\d)h?$/);
  // `2` — a bare integer, read as hours.
  const bare = text.match(/^(\d+)$/);

  if (hm) {
    seconds = Number(hm[1]) * 3600 + (hm[2] ? Number(hm[2]) * 60 : 0);
  } else if (m) {
    seconds = Number(m[1]) * 60;
  } else if (split) {
    // Sixty minutes and up is refused rather than carried into the next hour.
    // `5,60` looks exactly like five and six tenths of an hour — 5h 36m — and
    // reading it as six hours flat is the wrong-and-plausible answer this rule
    // exists to avoid. The `h`/`m` spelling still takes an overflow (`2h 90m`
    // is three and a half hours), because nobody writes that by accident.
    //
    // The count of digits is what picks the reading, decided 2026-09-09: one is
    // a tenth of an hour, two are minutes. So `1.5` is an hour and a half while
    // `1.50` is an hour and fifty, twenty minutes apart on two strings that look
    // almost alike. What makes it survivable is the same thing as everywhere
    // else here — the box spells back what it understood, `1h 30m` against
    // `1h 50m`, before anything is saved.
    if (Number(split[2]) >= 60) return { seconds: null, error: DURATION_NOT_A_NUMBER };
    seconds = Number(split[1]) * 3600 + Number(split[2]) * 60;
  } else if (tenth) {
    // One digit is a tenth of an hour, which is how Tempo reads it — measured by
    // Dmytro on 2026-09-09: `5.1` comes back as 5h 6m and `5.2` as 5h 12m. Tenths
    // land on whole minutes exactly (a tenth is six of them), so nothing rounds.
    seconds = Number(tenth[1]) * 3600 + Number(tenth[2]) * 360;
  } else if (bare) {
    seconds = Number(bare[1]) * 3600;
  } else {
    return { seconds: null, error: DURATION_NOT_A_NUMBER };
  }

  if (seconds === 0) return { seconds: null, error: DURATION_REQUIRED };
  if (seconds >= MAX_DURATION_SECONDS) {
    return { seconds: null, error: durationExceedsLimit(Math.floor(seconds / 3600)) };
  }
  return { seconds, error: null };
}

/**
 * Normalise a time box, or reject.
 *
 * `9` → `09:00`, `1000` → `10:00`, `1007` → `10:07`, and an explicit `H:mm` /
 * `HH:mm` is validated. A three-digit `930` is ambiguous and refused, as is an
 * hour past 23 (`2500`) or anything non-numeric. A refused value reverts to the
 * box's previous contents rather than showing an error — once settled, the field
 * never holds a time that is not a time (what is being typed is on screen
 * verbatim until then, as in any text box).
 *
 * Returns the `HH:mm` string, or null to mean "revert".
 */
export function parseTime(raw: string): string | null {
  const text = raw.trim();

  const colon = text.match(/^(\d{1,2}):(\d{2})$/);
  if (colon) return buildTime(Number(colon[1]), Number(colon[2]));

  if (!/^\d+$/.test(text)) return null;

  if (text.length <= 2) return buildTime(Number(text), 0);
  if (text.length === 4) return buildTime(Number(text.slice(0, 2)), Number(text.slice(2)));
  // Three digits (`930`) is ambiguous between 9:30 and 93:0; five or more is not
  // a time. Both revert, as Tempo's form does.
  return null;
}

function buildTime(hours: number, minutes: number): string | null {
  if (hours > 23 || minutes > 59) return null;
  return `${pad(hours)}:${pad(minutes)}`;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** Minutes since 00:00 for a normalised `HH:mm`. */
function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** A minute-of-day back to `HH:mm`, wrapping across midnight. */
/**
 * Minutes into the day a settled End names.
 *
 * `00:00` can only be the END of the day now that a row may not cross midnight:
 * a row that begins and ends at midnight has no length, so the other reading is
 * not a reading at all. Which is why `1440` is a value End is allowed to hold
 * while Start never is. [HW-891]
 */
function endMinutesOf(end: string): number {
  const minutes = toMinutes(end);
  return minutes === 0 ? MINUTES_PER_DAY : minutes;
}

/**
 * The latest a row of this length can begin and still finish inside its day.
 *
 * This is the whole of the no-crossing rule on the two paths that can push the
 * end past midnight — typing a Duration, and moving the Start. Duration is the
 * anchor value, so it is the Start that gives way: `22:00` with `4h` becomes
 * `20:00 — 00:00` rather than spilling into tomorrow. Agreed at the review of
 * 2026-09-09. [HW-891]
 */
function startWithinDay(startMinutes: number, durationMinutes: number): number {
  return Math.min(startMinutes, MINUTES_PER_DAY - durationMinutes);
}

/**
 * Whether the reader's own duration still fits between midnight and this End.
 *
 * The one case the rule cannot fix by moving the Start, because there is no
 * earlier start to move to — End `02:00` with `3h` typed would have to begin at
 * eleven the night before. See `endRefusal`, which is how the box says so.
 */
function endLeavesRoom(endMinutes: number, durationMinutes: number): boolean {
  return endMinutes - durationMinutes >= 0;
}

function fromMinutes(minutes: number): string {
  const wrapped = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`;
}

/**
 * Apply one edit and relink the row. Pure: same input, same output, no clock
 * read. `value` is the raw text the edited field was left holding.
 *
 * The rules, Duration the anchor value and Start the anchor time:
 *
 *  - Duration edited → End = Start + Duration; Start held. A cleared or `0`
 *    Duration sets End = Start and marks the row as needing a Duration.
 *  - Start edited → End = Start + Duration; Duration held. An unreadable time
 *    reverts.
 *  - End edited, Duration was the user's → Start = End − Duration (wrapping
 *    midnight); Duration held.
 *  - End edited, Duration absent or itself derived → Duration = End − Start;
 *    Start held. An End before Start instead sets Start = End and clears
 *    Duration.
 */
export function linkTimes(
  state: LinkedTimeState,
  field: LinkedField,
  value: string
): LinkedTimeState {
  if (field === 'duration') return linkFromDuration(state, value);
  if (field === 'start') return linkFromStart(state, value);
  return linkFromEnd(state, value);
}

function linkFromDuration(state: LinkedTimeState, value: string): LinkedTimeState {
  const reading = parseDuration(value);

  // Cleared or `0`: nothing to anchor End to, so it collapses onto Start and the
  // row is marked `cleared` — which is what makes the caller say "This field is
  // required" about a box the reader emptied, while a box never filled stays
  // silent.
  if (value.trim() === '' || reading.error === DURATION_REQUIRED) {
    return { ...state, duration: value, durationSource: 'cleared', end: state.start };
  }

  // Unreadable but not empty (`1:30`, `1d`): keep the text so its error can be
  // shown, and leave the interval where it was rather than recomputing from
  // nonsense.
  if (reading.seconds === null) {
    return { ...state, duration: value };
  }

  // Read, so it is spelled back in the field's own words: `90m` settles as
  // `1h 30m`, `2` as `2h`, `5,18` as `5h 18m`. Tempo does this, and it is what
  // teaches the grammar — the box answers what it understood, so nobody has to
  // be told in advance that minutes need an `m`. It also ends a smaller
  // inconsistency: a duration this module DERIVED was already printed
  // canonically, so only the typed one looked different. [HW-891]
  const minutes = reading.seconds / 60;
  const start = startWithinDay(toMinutes(state.start), minutes);
  return {
    ...state,
    duration: formatDuration(reading.seconds),
    durationSource: 'user',
    start: fromMinutes(start),
    end: fromMinutes(start + minutes),
  };
}

function linkFromStart(state: LinkedTimeState, value: string): LinkedTimeState {
  const start = parseTime(value);
  if (start === null) return state; // revert

  const reading = parseDuration(state.duration);
  // With no usable Duration the End simply tracks Start, so the two never sit
  // crossed. With one, the pair slides back off the day boundary together: a
  // Start typed at 22:00 against 4h settles as 20:00, because the length is
  // what the reader meant and tomorrow is not somewhere this entry may go.
  if (reading.seconds === null) return { ...state, start, end: start };
  const minutes = reading.seconds / 60;
  const startMinutes = startWithinDay(toMinutes(start), minutes);
  return {
    ...state,
    start: fromMinutes(startMinutes),
    end: fromMinutes(startMinutes + minutes),
  };
}

function linkFromEnd(state: LinkedTimeState, value: string): LinkedTimeState {
  const end = parseTime(value);
  if (end === null) return state; // revert

  const reading = parseDuration(state.duration);

  const endMinutes = endMinutesOf(end);

  // Duration the user typed is sacred: End moving means Start moves under it.
  // Unless there is no room left before midnight to move it into — then the row
  // stands as it was and the box says why, which is `endRefusal`'s business.
  // Folding it into the day instead would save a figure nobody asked for, and
  // the likeliest truth behind End 02:00 against 3h is a mistyped date.
  if (state.durationSource === 'user' && reading.seconds !== null) {
    const minutes = reading.seconds / 60;
    if (!endLeavesRoom(endMinutes, minutes)) return state; // refused
    return { ...state, end, start: fromMinutes(endMinutes - minutes) };
  }

  // Otherwise Duration is what End defines.
  const span = endMinutes - toMinutes(state.start);

  // An End at or before the Start cannot be a length of work — a row may no
  // longer run round the clock into tomorrow — so Start snaps to End and the row
  // is left needing a Duration: `cleared`, not `empty`, because the reader did
  // not empty that box themselves and is owed the reason Save just died.
  if (span <= 0) {
    return { ...state, end, start: end, duration: '', durationSource: 'cleared' };
  }
  return { ...state, end, duration: formatDuration(span * 60), durationSource: 'derived' };
}

/**
 * Why moving the End here has to be refused, or null when it can be honoured.
 *
 * The row's only refusal, and the only thing a time box ever explains — an
 * unreadable time still reverts without a word, because nothing wrong is left on
 * screen to explain. Here something is: the reader asked for an end their own
 * duration cannot reach without starting yesterday.
 *
 * Reads the same condition `linkFromEnd` refuses on, so the sentence and the
 * silence cannot come apart. [HW-891]
 */
export function endRefusal(state: LinkedTimeState, value: string): string | null {
  const end = parseTime(value);
  if (end === null) return null;
  const reading = parseDuration(state.duration);
  if (state.durationSource !== 'user' || reading.seconds === null) return null;
  return endLeavesRoom(endMinutesOf(end), reading.seconds / 60) ? null : STARTS_DAY_BEFORE;
}

/**
 * The message owed under the Duration box, or null for silence.
 *
 * A box holding text is judged on the text. An empty one is judged on how it
 * came to be empty: emptied by the reader, or by an End that landed before the
 * Start, it is owed the reason the Save button just died; never filled in at
 * all — a dialog just opened — it has nothing to answer for yet.
 *
 * Here rather than in the row component because it is the whole payoff of the
 * `empty`/`cleared` distinction, and a rule that decides whether a form scolds
 * somebody deserves a test. [HW-891]
 */
export function durationMessage(state: LinkedTimeState, typing = false): string | null {
  // An emptied box answers at once, typing or not, because Save dies at the
  // same keystroke: `editEntryTimes` refuses a `cleared` duration, so a message
  // held back until the blur left a dead button standing over a silent field —
  // the one mismatch this module exists to remove. Nothing is spoiled by saying
  // it early either: an empty box is not a value on its way anywhere.
  if (state.duration.trim() === '') {
    return state.durationSource === 'cleared' ? DURATION_REQUIRED : null;
  }
  // A value being typed is not judged yet. Judging every keystroke means
  // scolding somebody who is typing correctly: `2h 30m` passes through "2h 3",
  // which reads as no duration at all, so the message flashed up in the middle
  // of a word and went away again on the last character. The box settles on
  // blur — spelling itself back — and answers then. [HW-891]
  if (typing) return null;
  return parseDuration(state.duration).error;
}

/**
 * The row as a whole: the three linked values, plus the two things the fields
 * know that the values do not.
 *
 * It lives here rather than in the component because every one of the rules
 * below was a measured defect first, and a rule that decides which figure gets
 * saved has to be testable — this repo's vitest runs in a node environment and
 * cannot mount a React component, so anything left inline is untestable by
 * construction. [HW-891]
 */
export interface RowState {
  linked: LinkedTimeState;
  /** A time box mid-typing: its text has not reached `linked` yet, so the box
   *  can show `13` without the row believing the start is one in the morning. */
  draft: { field: 'start' | 'end'; text: string } | null;
  /** Whether the Duration box has been typed in since it last settled. */
  durationTyped: boolean;
  /**
   * The End the row would not take, still on screen with the reason under it.
   *
   * It carries its own `text` rather than leaning on `draft`, which means one
   * thing only: the box being typed in. Keeping a refused value in `draft`
   * instead gave that slot a second meaning and three ways to go wrong — the
   * Duration could settle around it, leaving three boxes that disagreed
   * (`2h`, `01:00`, `0230`) with Save alive; a submit then replayed the stale
   * text and filed a start nobody had seen (measured: shown 01:00, saved
   * 00:30); and typing in the Start box overwrote the draft, losing the refused
   * End without a word.
   *
   * Cleared by retyping the End, re-judged when the Duration settles — those
   * are the two ways out, and both are the reader's own edits.
   */
  rejected: { field: 'end'; text: string; message: string } | null;
}

export type RowEvent =
  | { kind: 'type-duration'; text: string }
  | { kind: 'settle-duration' }
  | { kind: 'type-time'; field: 'start' | 'end'; text: string }
  | { kind: 'settle-time'; field: 'start' | 'end' };

/**
 * One event, one new row — or the very same row when the event changes nothing,
 * so a caller can compare by identity and skip the render.
 *
 * The three rules that were bugs before they were rules:
 *
 *  - Typing in the Duration box makes the value the READER'S there and then.
 *    Waiting for the blur to say so meant a duration typed but not yet settled
 *    still counted as absent, and the next End edit overwrote it instead of
 *    moving the Start: `3h` typed, a save refused by the server, End set to
 *    14:00, and five hours saved where three were meant.
 *  - Emptying it by hand marks the row as ASKING for a duration, which is what
 *    puts "This field is required" under the box at the keystroke rather than a
 *    blur later: Save goes dead on that same keystroke, so the field has to say
 *    why. A value merely unfinished, `2h 3` on its way to `2h 30m`, is judged on
 *    the blur instead — see `durationMessage`.
 *  - Settling relinks only if something was typed. A blur that follows a mere
 *    visit to the box used to relink anyway, and a relink rewrites provenance:
 *    a duration the End had filled in was promoted to the reader's own, so the
 *    next End edit slid the Start instead of recomputing the length; and in the
 *    two dialogs an untouched empty box printed "This field is required" the
 *    moment focus left it — in Log Time, on the first click into the project
 *    picker, because its Duration box holds the focus on open.
 */
export function applyRowEvent(row: RowState, event: RowEvent): RowState {
  switch (event.kind) {
    case 'type-duration':
      // A standing refusal is left alone here: it is still true of the duration
      // that is settled, and it is `settle-duration` that re-judges it. Dropping
      // the message on the keystroke instead cleared the reason while the value
      // it was about stayed on screen, and woke Save over a row whose three
      // boxes did not agree.
      return {
        ...row,
        linked: {
          ...row.linked,
          duration: event.text,
          durationSource: event.text.trim() === '' ? 'cleared' : 'user',
        },
        durationTyped: true,
      };

    case 'settle-duration':
      if (!row.durationTyped) return row;
      return rejudge({
        ...row,
        linked: linkTimes(row.linked, 'duration', row.linked.duration),
        durationTyped: false,
      });

    case 'type-time':
      // Retyping the End is the other way out, so it takes the refusal with it.
      // Typing in the Start box does not: the refusal is about the End and the
      // Duration, and the Start has no say in whether it fits.
      return {
        ...row,
        draft: { field: event.field, text: event.text },
        rejected: event.field === 'end' ? null : row.rejected,
      };

    case 'settle-time': {
      if (!row.draft || row.draft.field !== event.field) return row;
      // Refused: the row stands and the value stays on screen with the reason
      // under it — exactly how the Duration box already treats `1:30`. It moves
      // out of `draft` and into `rejected`, so `draft` goes on meaning only the
      // box being typed in.
      const text = row.draft.text;
      const message = event.field === 'end' ? endRefusal(row.linked, text) : null;
      if (message !== null) {
        return { ...row, draft: null, rejected: { field: 'end', text, message } };
      }
      return {
        ...row,
        linked: linkTimes(row.linked, event.field, text),
        draft: null,
        rejected: event.field === 'end' ? null : row.rejected,
      };
    }
  }
}

/**
 * The row as it stands once every box still being typed in has had its say.
 *
 * A time box reaches `linked` on its blur, and clicking a button does not move
 * focus on Safari or on Firefox for macOS — so a Start typed as `13` and sent
 * with the button was never settled, and the entry went in at the old 09:00
 * without a word: the mouse's way back into the bug that `enterSettlesTime`
 * closes for the keyboard. Both submit handlers now read the row through here
 * rather than reading `linked` directly. Measured on this branch: with the
 * button clicked from a Start box that still held the focus, the entry saved
 * 09:00 before and 13:00 after. [HW-891]
 *
 * Duration first, then the time box: a `draft` exists only while its box is
 * being typed in, which means that box holds the focus, which means the
 * Duration box has already blurred and settled — so when both look pending the
 * time edit is the later one, and it must be the one that gets the last word.
 * Settling the Duration also re-judges a standing refusal, so one click of the
 * button both shortens the length and takes the End that now fits.
 *
 * Returns the very same row when there was nothing to settle, so a caller can
 * compare by identity and leave its state alone.
 *
 * A settle can be REFUSED — see `endRefusal` — and then the row it hands back
 * carries `rejected`, the values it already had, and the draft still showing the
 * value that was not taken. Save is dead while that stands, and a submit checks
 * it too: the form can still be submitted with Enter from a box that is not a
 * button.
 */
export function settleRow(row: RowState): RowState {
  const settled = applyRowEvent(row, { kind: 'settle-duration' });
  return row.draft
    ? applyRowEvent(settled, { kind: 'settle-time', field: row.draft.field })
    : settled;
}

/** What a time box should display: what is being typed, else the settled value. */
export function timeBoxValue(row: RowState, field: 'start' | 'end'): string {
  if (row.draft && row.draft.field === field) return row.draft.text;
  if (row.rejected && row.rejected.field === field) return row.rejected.text;
  return row.linked[field];
}

/**
 * A standing refusal, judged again against the row as it now stands.
 *
 * The Duration box is one of the two ways out of a refusal, so settling it has
 * to re-ask the question: `02:00` refused against `3h` is `02:00` accepted
 * against `2h`, and the row should say so there and then rather than leaving a
 * value on screen that the next submit would quietly apply.
 */
function rejudge(row: RowState): RowState {
  if (!row.rejected) return row;
  // Only a duration that READS can answer the question. `endRefusal` says null
  // for one that does not — meaning "nothing here to refuse" — and taking that
  // for "it fits" applied the End and let `linkFromEnd` derive a length from it:
  // a reader who typed an unreadable duration over a refused `05:00` got `4h`, a
  // figure nobody chose, with both messages gone and Save alive. (Measured with
  // `1.5h`, which the grammar took later; `1:30` reaches it today.) Emptying the
  // box did the same
  // — "This field is required" at the keystroke, then quietly filled in on blur.
  // The refusal stands until the Duration is something the row can read.
  if (parseDuration(row.linked.duration).seconds === null) return row;
  const message = endRefusal(row.linked, row.rejected.text);
  if (message !== null) return { ...row, rejected: { ...row.rejected, message } };
  return { ...row, linked: linkTimes(row.linked, 'end', row.rejected.text), rejected: null };
}

/**
 * Does Enter in this time box have a value to settle rather than a form to
 * submit? Log Time is a real `<form>`, and Enter submits it without any blur
 * firing, so a typed 13:00 was measured arriving in the database as 09:00 with
 * the dialog closing as if all was well. The first Enter settles, the next one
 * saves — with the interval by then on screen.
 */
export function enterSettlesTime(row: RowState, field: 'start' | 'end'): boolean {
  return row.draft !== null && row.draft.field === field;
}

/** What an entry already stored looks like to this row. */
export interface StoredEntryTimes {
  /** The entry's `startedAt`, as the API hands it over. */
  startedAt: string;
  /** Its length in seconds. */
  duration: number;
  /**
   * The zone its wall digits were written in — null for a row from before the
   * wall-time model, whose `startedAt` is still a real instant rather than wall
   * digits, and is read through the viewer's zone until the backfill rewrites
   * it. Required, so no caller can drop it and have a converted row read as
   * legacy by accident. [HW-916]
   */
  timezone: string | null;
}

/** A stored entry as Edit Time Entry saves it. [HW-916] */
export type EditableEntryTimes = StoredEntryTimes;

/**
 * The row and the Date box, seeded from the wall-clock digits the entry holds.
 *
 * Date and Start are the UTC digits of `startedAt`, which under the wall-time
 * model ARE the digits its author typed — so the dialog opens on the same
 * 09:00 in every viewer's browser, instead of converting it through the host's
 * zone as the local-time seed of ICRM-BUG-203 did. [HW-916] A legacy row (null
 * zone) is the exception: its `startedAt` is still a real instant until the
 * backfill rewrites it, which runs after this frontend ships and cannot reach
 * rows a stale tab writes later, so it opens on that instant read in the
 * viewer's own zone — the reading the timesheet row prints for it, and the one
 * it opened on before HW-916. Duration comes from the stored length, and End
 * is derived from the two, so the dialog opens showing the interval the
 * timesheet row prints.
 *
 * A sub-minute entry (only a timer writes one) has no minute to show: its
 * Duration seeds empty rather than as a false "0m", and `entryInstants` sends
 * the stored length back untouched.
 *
 * The seeded length counts as DERIVED rather than as the reader's own, because
 * they did not type it — and that decides what editing the End does. Held as
 * the reader's, an End edit slid the Start underneath it: an entry of 01:00 —
 * 05:00 whose End was corrected to 02:00 came back as 22:00 — 02:00, four hours
 * long as before but twenty-one hours later in the day, with the Date box never
 * moving and nothing on screen saying so. Derived, the same edit does what the
 * words mean — it ends at two, so it is an hour long.
 *
 * `viewerZone` is for a caller that already resolved it once for a whole list
 * — `canDuplicateEntry` seeds one entry per row on every render, so the Weekly
 * Timesheet hands over the zone it already read for the week rather than
 * making each row ask again. Left out, as at the single dialogs that call
 * this, the zone is looked up here instead. [HW-916]
 */
export function seedLinkedTime(entry: StoredEntryTimes, viewerZone?: string): {
  state: LinkedTimeState;
  dateKey: string;
} {
  const wall = entryStartWall(entry, viewerZone);
  const start = entryClock(wall);
  const base: LinkedTimeState = { duration: '', start, end: start, durationSource: 'empty' };
  const durationText = entry.duration >= 60 ? formatDuration(entry.duration) : '';
  // The end is built from the stored length directly rather than through
  // `linkTimes`, whose job now includes holding a row inside its day. A row
  // already stored across midnight — the timer writes them, and the ones from
  // before this rule are still there — opens reading exactly what it is, and is
  // saved back untouched unless the reader moves one of the three values. Only
  // then does the no-crossing rule reach it. [HW-891]
  const reading = durationText ? parseDuration(durationText) : null;
  const linked: LinkedTimeState = durationText
    ? {
        duration: durationText,
        start,
        end:
          reading && reading.seconds !== null
            ? fromMinutes(toMinutes(start) + reading.seconds / 60)
            : start,
        durationSource: 'derived',
      }
    : base;
  return { state: linked, dateKey: entryDayKey(wall) };
}

/**
 * The instant a Date box and a Start box name together — their digits written
 * as UTC, not converted through the host's zone, because the stored `startedAt`
 * is the wall clock the reader typed [HW-916] — or null when they name none: a
 * date box left empty or half-typed, which is the state the dialogs print
 * "Pick a date for this entry." about.
 */
export function readInstant(dateKey: string, start: string): Date | null {
  return wallInstant(dateKey, start);
}

/**
 * The pair of instants a save sends: the start the reader settled on, and the
 * end that is always start + duration, so the stored span matches the length
 * the API enforces. A span past midnight lands on the next calendar day while
 * the entry keeps counting on its start date.
 *
 * `keepSecondsOf` carries the seconds an entry already holds over a save. The
 * Start box is minute-resolution, so rebuilding the instant from it alone moves
 * a timer's 14:23:47 entry to 14:23:00 — measured, on a save that only touched
 * the note. Pass the stored `startedAt` while the reader has left the CLOCK
 * alone, whatever they did to the date: moving that entry to the next day
 * should carry its 47 seconds along, not quietly round them off. Pass nothing
 * once the clock itself has been retyped, because then the minute chosen is the
 * whole of what is meant.
 *
 * Null when the date and start name no instant, as `readInstant` reports it.
 */
export function entryInstants(input: {
  dateKey: string;
  start: string;
  durationSeconds: number;
  keepSecondsOf?: string | null;
}): { startedAt: string; endedAt: string } | null {
  const { dateKey, start, durationSeconds, keepSecondsOf } = input;
  const startedAt = readInstant(dateKey, start);
  if (!startedAt) return null;

  if (keepSecondsOf) {
    const stored = new Date(keepSecondsOf);
    if (!Number.isNaN(stored.getTime())) {
      // UTC fields on both sides, because the stored digits are UTC digits and
      // this module never reads through the host's zone. The local pair only
      // differs for an offset that is not whole minutes. [HW-916]
      startedAt.setUTCSeconds(stored.getUTCSeconds(), stored.getUTCMilliseconds());
    }
  }

  return {
    startedAt: startedAt.toISOString(),
    endedAt: new Date(startedAt.getTime() + durationSeconds * 1000).toISOString(),
  };
}

/** What Edit Time Entry sends for the times — see `editEntryTimes` for when the zone rides along. */
export interface EditPayload {
  duration: number;
  startedAt: string;
  endedAt: string;
  timezone?: string;
}

/**
 * Everything Edit Time Entry sends, or null when it may not be sent at all —
 * so the Save button and the payload are decided by one rule rather than by
 * three expressions in the dialog.
 *
 * An untouched Duration is judged by what is STORED rather than by what the box
 * can read: a timer's twelve-second entry has no minute to show and is
 * perfectly legal, while a thirty-hour row — which only the timer or the
 * Clockify import can have written [HW-886] — is not, and offering Save for it
 * only spends a round trip to be told so. A touched one is judged by the box.
 *
 * The stored length is then sent exactly as it stands, seconds included, so a
 * note edit cannot round recorded work away. The End box, being minute-
 * resolution, can therefore print a minute earlier than the saved end: an entry
 * started at 09:00:13 and lasting 2h 0m 47s shows 09:00 — 11:00 while its end
 * is 11:01. The trade is deliberate — those seconds are somebody's work — but
 * it does mean the row and the dialog can disagree by a minute on such an entry.
 *
 * An entry shorter than a minute has no Duration text at all, so editing only
 * its Start collapses the End onto it and the row shows no length while the
 * stored seconds are still what gets saved. That is the truthful rendering at
 * minute resolution: rounding those seconds to a minute, or to nothing, would
 * falsify recorded work to make a box look tidy.
 *
 * A row nothing has moved is sent at exactly the instant it carries, without
 * rebuilding it from the boxes at all. Rebuilding is not a no-op even when the
 * boxes still read the same: in the hour a zone repeats each autumn, one wall
 * clock names two instants, and `09:00` resolves to the first of them — so an
 * entry stored in the second, saved after a note edit, moved an hour. Measured
 * in Europe/Kyiv: stored 01:30Z came back 00:30Z. Wall digits have no repeated
 * hour, but the pass-through still matters for a legacy row: it keeps that
 * row's real instant byte for byte.
 *
 * The zone is sent exactly when this save REBUILDS the start from the boxes,
 * and never on a pass-through. The server refuses a changed `startedAt` on a
 * zoned row that comes without a zone, and a zone that comes without a new
 * start, so the two travel together. Which zone:
 *
 *  - A zoned row re-sends its OWN stored zone, whoever is editing: its digits
 *    are its author's clock, and the dialog showed them as such. Sending the
 *    viewer's would silently move the entry into another zone — the UI never
 *    changes an entry's zone.
 *  - A legacy row (null `timezone`) is stamped with the viewer's zone, since
 *    the digits it is rebuilt from are the ones the dialog showed — the legacy
 *    instant read in the viewer's zone, see `seedLinkedTime`. Left unstamped,
 *    the backfill would convert those wall digits a second time.
 *
 * A row passed through untouched sends no zone: a legacy one stays legacy, its
 * instant re-sent byte for byte for the backfill to convert once, and a zoned
 * one re-sends its stored `startedAt` unchanged, which the server accepts
 * without one. [HW-916]
 */
export function editEntryTimes(input: {
  stored: EditableEntryTimes;
  state: LinkedTimeState;
  dateKey: string;
  /** The Start the dialog opened on, to tell a retyped clock from an untouched one. */
  seedStart: string;
  durationEdited: boolean;
}): EditPayload | null {
  const { stored, state, dateKey, seedStart, durationEdited } = input;

  // A box that is asking for a value is not a value. Without this the screen
  // and the button could disagree: an End dragged before the Start clears the
  // Duration of a sub-minute entry, whose box was empty to begin with, so
  // "was it edited" answered no, the stored twelve seconds looked usable, and
  // Save stayed alive under a field printing "This field is required".
  if (state.durationSource === 'cleared') return null;

  const duration = durationEdited ? parseDuration(state.duration).seconds : stored.duration;
  const usable = durationEdited ? duration !== null : durationWithinLimit(stored.duration);
  if (!usable || duration === null) return null;

  const clockUntouched = state.start === seedStart;
  // The Date box was seeded from the legacy-aware reading, so "untouched" is
  // judged against that same reading: compared with a legacy row's raw digits,
  // a Kyiv viewer's untouched 00:30 would look moved and be rebuilt. [HW-916]
  // `== null`: an entry shape with no zone at all is a legacy row too. The
  // viewer's zone is resolved once, for the reading below and for the stamp.
  const viewerZone = stored.timezone == null ? viewerTimeZone() : undefined;
  const storedWall = entryStartWall(stored, viewerZone);
  if (clockUntouched && dateKey === entryDayKey(storedWall)) {
    const startedAt = new Date(stored.startedAt);
    return {
      duration,
      startedAt: startedAt.toISOString(),
      endedAt: new Date(startedAt.getTime() + duration * 1000).toISOString(),
    };
  }

  const instants = entryInstants({
    dateKey,
    start: state.start,
    durationSeconds: duration,
    keepSecondsOf: clockUntouched ? storedWall : null,
  });
  if (!instants) return null;
  return { duration, ...instants, timezone: stored.timezone ?? viewerZone ?? viewerTimeZone() };
}

/**
 * One row, settled, and what a save of it would send — or null when it may not
 * be sent at all.
 *
 * THE POINT IS THAT THERE IS ONE OF THESE. The button's liveness and the submit
 * handler's payload were two expressions over the same row, and they drifted
 * twice in a day: first Save stayed shut over a refusal a typed Duration had
 * already resolved, then — after that half was fixed — the button went on
 * reading the UNSETTLED duration, so a fresh dialog with an End typed and never
 * blurred showed three filled boxes, a dead button and no message at all, while
 * `settleRow` had `8h, 09:00 — 17:00` in hand. Both bugs bite only where a click
 * does not blur, which is Safari and Firefox for macOS.
 *
 * It lives here rather than in the dialogs for the reason the whole module does:
 * this repo's vitest is node-env and cannot mount a component, so a rule left
 * inline in a `.tsx` cannot be tested, and the second bug got through precisely
 * because it was. [HW-891]
 *
 * `row` comes back settled whatever the answer, and the caller should set it
 * back either way — that is what puts a refusal, or a spelled-back duration, on
 * screen when a submit is what triggered it.
 */
export interface RowSubmission<Payload> {
  row: RowState;
  payload: Payload | null;
}

/** What Log Time sends for a row, or null when it may not be sent. */
export function logSubmission(
  row: RowState,
  dateKey: string
): RowSubmission<{ startedAt: string; endedAt: string; duration: number; timezone: string }> {
  const settled = settleRow(row);
  if (settled.rejected) return { row: settled, payload: null };

  const duration = parseDuration(settled.linked.duration).seconds;
  if (duration === null) return { row: settled, payload: null };

  const instants = entryInstants({
    dateKey,
    start: settled.linked.start,
    durationSeconds: duration,
  });
  // Every new entry carries the zone its wall digits were typed in. [HW-916]
  return {
    row: settled,
    payload: instants ? { ...instants, duration, timezone: viewerTimeZone() } : null,
  };
}

/** What Edit Time Entry sends for a row, or null when it may not be sent. */
export function editSubmission(input: {
  row: RowState;
  stored: EditableEntryTimes;
  dateKey: string;
  seedStart: string;
  durationEdited: boolean;
}): RowSubmission<EditPayload> & {
  /** `durationEdited` as the settle leaves it — a settle can move the Duration,
   *  and then what is stored is no longer what should be sent. */
  durationEdited: boolean;
} {
  const { row, stored, dateKey, seedStart } = input;
  const settled = settleRow(row);
  const durationEdited =
    input.durationEdited || settled.linked.duration !== row.linked.duration;
  if (settled.rejected) return { row: settled, durationEdited, payload: null };

  return {
    row: settled,
    durationEdited,
    payload: editEntryTimes({
      stored,
      state: settled.linked,
      dateKey,
      seedStart,
      durationEdited,
    }),
  };
}

/**
 * What a Logged Hours cell of Reports → Show entries sends for a new length:
 * the length alone while the entry still ends inside its day, or the length
 * with the start moved back when it would not.
 *
 * The cell has no Start box, so it follows the rule the Duration box of the
 * Edit dialog follows (HW-891): Duration is the anchor and the Start gives
 * way (`startWithinDay`). Run through the dialog's own steps — the row seeded
 * as the dialog seeds it (the author's digits, or the viewer's reading of a
 * legacy row), the Duration typed into it, the times read off as its Save
 * would — so the cell and the dialog cannot judge one entry's day apart, and
 * a moved start carries the zone the dialog would stamp. A length the day
 * cannot hold at all is refused before this, by `parseDuration`: anything
 * under 24h fits once the start reaches midnight.
 *
 * Null when the entry's stored start names no day, which only a malformed
 * row could. [HW-974]
 */
export function inlineDurationPayload(
  stored: EditableEntryTimes,
  seconds: number
): { duration: number } | EditPayload | null {
  const seed = seedLinkedTime(stored);
  const linked = linkTimes(seed.state, 'duration', formatDuration(seconds));
  if (linked.start === seed.state.start) return { duration: seconds };
  return editEntryTimes({
    stored,
    state: linked,
    dateKey: seed.dateKey,
    seedStart: seed.state.start,
    durationEdited: true,
  });
}

/**
 * The two sentences the row is showing, if any.
 *
 * Both judge the SETTLED row, because that is what the buttons judge — read raw
 * they went the other way and an emptied Duration printed "This field is
 * required" over a live Save. Two things stay on the RAW row, and each is a
 * separate rule about not interrupting somebody mid-word:
 *
 *  - `durationTyped`, which is what says the Duration box is being typed in.
 *  - A pending End draft. Settling one on every keystroke means judging every
 *    keystroke: with `8h` in the row, retyping the End as `1700` refused the
 *    bare `1` and flashed "That would start this entry the day before" under a
 *    box the reader was three characters from finishing. While that box is being
 *    typed in, only a refusal it ALREADY stood under has anything to say — and
 *    `type-time` clears that, because retyping the End is how you resolve one.
 *
 * Here rather than in the component for the reason the rest of the module is:
 * vitest is node-env here and cannot mount a component, and the last two bugs in
 * this area were both rules left inline in a `.tsx`. [HW-891]
 */
export function rowMessages(row: RowState): { duration: string | null; end: string | null } {
  const settled = settleRow(row);
  const refusal = row.draft?.field === 'end' ? row.rejected : settled.rejected;
  return {
    duration: durationMessage(settled.linked, row.durationTyped),
    end: refusal?.message ?? null,
  };
}
